// src/services/ConfigTemplateService.js
// Manages reusable configuration templates for Admin settings.
// ADM-206: backend dual-write (Wave 27 demo-29) via /api/configtemplates/state.

import storage from './StorageAdapter';
import auditService from './AuditService';
import { applyDualWriteMixin, makeDualWriteMixin } from './_dualWriteHelpers';
import configTemplatesApi from '../api/configTemplates';

const STORAGE_KEY = 'rms_config_templates';
const STATE_KEY = 'templates';

class ConfigTemplateService {
  constructor() {
    this.listeners = [];
  }

  _entityKey(_baseKey) {
    return STORAGE_KEY;
  }

  _load() {
    return storage.getJSON(STORAGE_KEY, []);
  }

  async _save(templates) {
    await this._dualWriteJson(STATE_KEY, templates);
    this._notify();
  }

  subscribe(fn) {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter(l => l !== fn);
    };
  }

  notifyListeners() {
    this.listeners.forEach(fn => fn());
  }

  _notify() { this.notifyListeners(); }

  getTemplates() {
    return this._load();
  }

  getTemplate(id) {
    return this._load().find(t => t.id === id) || null;
  }

  async saveTemplate(name, description, settings) {
    const templates = this._load();
    const id = `tpl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const now = new Date().toISOString();
    const template = {
      id,
      name: name.trim(),
      description: (description || '').trim(),
      createdAt: now,
      settings
    };
    templates.push(template);
    await this._save(templates);
    auditService.logCreate('Admin', 'ConfigTemplate', id, { name: template.name }, 'Config template created');
    return template;
  }

  async deleteTemplate(id) {
    const templates = this._load().filter(t => t.id !== id);
    await this._save(templates);
    auditService.logDelete('Admin', 'ConfigTemplate', id, 'Config template deleted');
  }

  getTemplateSettings(id) {
    const template = this.getTemplate(id);
    return template ? template.settings : null;
  }

  async initFromBackend() {
    return this._initFromBackend([STATE_KEY], []);
  }
}

applyDualWriteMixin(ConfigTemplateService, makeDualWriteMixin({
  storage,
  api: configTemplatesApi,
  serviceName: 'ConfigTemplateService',
}));

const configTemplateService = new ConfigTemplateService();
export default configTemplateService;
